import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SparklineChart from '../components/sparkline-chart';
import Loader from '../components/loader';
import ErrorBanner from '../components/error-banner';
import OnboardingTour from '../components/onboarding-tour';
import { COIN_DETAIL_TOUR_STEPS } from '../config/onboarding-steps';
import { useAuth } from '../context/auth-context';
import { fetchTopCoins, fetchCoinPriceHistory } from '../services/coingecko-api';
import { fetchKlinesAllTimeframes } from '../services/binance-api';
import { fetchCoinbaseKlinesAllTimeframes } from '../services/coinbase-api';
import { isServerApiAvailable, fetchCoinInfo, fetchServerCoinDetail } from '../services/server-api';
import { getBinanceSymbol, getCoinbaseSymbol, getKrakenSymbol, getOKXSymbol, isStablecoin } from '../utils/symbol-mapper';
import {
  calculateRSI,
  calculateMACD,
  calculateStochastic
} from '../utils/technical-indicators';
import { calculateTrend } from '../utils/trend-calculator';
import { parseBinanceKlines, parseBinanceKlinesForSparkline, extractPrices } from '../utils/timeframe-utils';
import {
  formatPrice,
  formatPercentChange,
  formatMarketCap,
  formatVolume,
} from '../utils/format-helpers';
import './coin-detail-page.css';

// --- TradingView chart ---

const TV_INTERVALS = [
  { label: '4H',    value: '240' },
  { label: 'Daily', value: 'D'   },
  { label: 'Weekly',value: 'W'   },
];

// studies_overrides is only honored by TradingView's official script-based
// widget constructor, not as a query param on the bare /widgetembed/ iframe
// URL (confirmed against TradingView's own docs, which describe it as a
// Widget Constructor option) — the previous iframe-only approach silently
// dropped the 50 SMA override on every coin. Loading tv.js reintroduces the
// script dependency the iframe approach was originally chosen to avoid, but
// it's the only mechanism TradingView actually documents for this.
let tvScriptPromise = null;
function loadTradingViewScript() {
  if (window.TradingView) return Promise.resolve();
  if (!tvScriptPromise) {
    tvScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load TradingView script'));
      document.head.appendChild(script);
    });
  }
  return tvScriptPromise;
}

let tvWidgetSeq = 0;

function TradingViewChart({ symbol, interval }) {
  const containerId = useRef(`tv-widget-${++tvWidgetSeq}`);
  const widgetRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(!!window.TradingView);

  useEffect(() => {
    let cancelled = false;
    loadTradingViewScript()
      .then(() => { if (!cancelled) setScriptReady(true); })
      .catch(err => console.error('[Detail] TradingView script load failed:', err.message));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!scriptReady || !symbol) return;

    widgetRef.current = new window.TradingView.widget({
      autosize:            true,
      symbol,
      interval,
      timezone:            'Etc/UTC',
      theme:               'dark',
      style:               '1',
      locale:              'en',
      toolbar_bg:          '#0a0e1a',
      enable_publishing:   false,
      hide_side_toolbar:   false,
      allow_symbol_change: false,
      save_image:          false,
      studies:             ['MASimple@tv-basicstudies'],
      studies_overrides:   { 'moving average.length': 50 },
      container_id:        containerId.current,
    });

    return () => {
      if (widgetRef.current && typeof widgetRef.current.remove === 'function') {
        try { widgetRef.current.remove(); } catch (e) { /* already torn down */ }
      }
      widgetRef.current = null;
    };
  }, [scriptReady, symbol, interval]);

  if (!symbol) return null;

  return <div id={containerId.current} className="tv-chart-inner" />;
}

// --- Volume sparkline ---

function VolumeSparkline({ volumes, avg }) {
  const W = 600, H = 60, PAD = 4;
  const max = Math.max(...volumes, avg * 2);
  const barW = (W - PAD * 2) / volumes.length - 1;
  const avgY = H - PAD - (avg / max) * (H - PAD * 2);

  return (
    <svg viewBox={"0 0 " + W + " " + H} xmlns="http://www.w3.org/2000/svg" className="volume-sparkline-svg">
      <line x1={PAD} y1={avgY} x2={W - PAD} y2={avgY}
        stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="4 3" />
      {volumes.map(function(v, i) {
        var barH = Math.max(2, (v / max) * (H - PAD * 2));
        var x = PAD + i * (barW + 1);
        var y = H - PAD - barH;
        var fill = v >= avg ? 'rgba(72,214,180,0.75)' : 'rgba(255,107,107,0.65)';
        return <rect key={i} x={x} y={y} width={barW} height={barH} fill={fill} rx="1" />;
      })}
    </svg>
  );
}

// --- About & News helpers ---

// CoinGecko descriptions are HTML — parse offscreen and read textContent only
// (never rendered via dangerouslySetInnerHTML) so no markup can execute.
function stripHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+\n/g, '\n').trim();
}

function formatNewsDate(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

const DESCRIPTION_PREVIEW_LENGTH = 500;

// --- Main page ---

function CoinDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showCoinDetailOnboarding, dismissCoinDetailOnboarding } = useAuth();
  const [coinData, setCoinData]           = useState(null);
  const [indicators, setIndicators]       = useState(null);
  const [sparklineData, setSparklineData] = useState([]);
  const [volumeStats, setVolumeStats]     = useState(null);
  const [tvSymbol, setTvSymbol]           = useState(null);
  const [chartInterval, setChartInterval] = useState('D');
  const [isLoading, setIsLoading]         = useState(true);
  const [error, setError]                 = useState(null);
  const [coinInfo, setCoinInfo]           = useState(null);
  const [coinInfoLoading, setCoinInfoLoading] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  useEffect(() => {
    fetchCoinData();
  }, [id]);

  // About & News section — independent of the OHLCV/indicator fetch above so
  // it still loads for coins with no exchange data. Only available in VPS mode.
  useEffect(() => {
    if (!id || !isServerApiAvailable()) return;
    let cancelled = false;
    setCoinInfo(null);
    setDescriptionExpanded(false);
    setCoinInfoLoading(true);

    fetchCoinInfo(id)
      .then((data) => { if (!cancelled) setCoinInfo(data); })
      .catch((err) => {
        console.warn('[Detail] coin info unavailable:', err.message);
        if (!cancelled) setCoinInfo(null);
      })
      .finally(() => { if (!cancelled) setCoinInfoLoading(false); });

    return () => { cancelled = true; };
  }, [id]);

  const plainDescription = useMemo(
    () => stripHtml(coinInfo?.description),
    [coinInfo]
  );

  const calculateTimeframeIndicators = (priceData, timeframeName) => {
    if (!priceData || !priceData.closes || priceData.closes.length < 30) {
      console.warn('[Detail] Insufficient data for ' + timeframeName + ' indicators');
      return { rsi: null, macd: null, stochastic: null, trend: 'N/A' };
    }
    const rsi        = calculateRSI(priceData.closes, 7);
    const macd       = calculateMACD(priceData.closes, 12, 26, 9);
    const stochastic = calculateStochastic(priceData.highs, priceData.lows, priceData.closes, 14, 3);
    const latestRSI      = rsi[rsi.length - 1];
    const latestMACDHist = macd.histogram[macd.histogram.length - 1];
    const latestStochK   = stochastic.k[stochastic.k.length - 1];
    const latestStochD   = stochastic.d[stochastic.d.length - 1];
    return {
      rsi,
      macd,
      stochastic,
      trend: calculateTrend(latestRSI, latestMACDHist, latestStochK, latestStochD),
    };
  };

  const fetchCoinData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('[Detail] Fetching data for: ' + id);

      const topCoins = await fetchTopCoins(250);
      const coinInfo = topCoins.find(function(coin) { return coin.id === id; });
      if (!coinInfo) {
        throw new Error('Cryptocurrency not found in top 250 by market cap');
      }
      setCoinData(coinInfo);

      if (isStablecoin(id)) {
        setSparklineData([]);
        setIndicators(null);
        return;
      }

      // Volume stats — VPS fast path. data-service.js computes volumeAvg200/
      // volumeRatio/volumeSpike/volumeHistory30d server-side for every coin,
      // regardless of which exchange (Binance/Coinbase/Kraken/OKX) it came
      // from, so this covers Kraken/OKX-only coins (e.g. Kaspa) that the
      // client-side Binance/Coinbase-only kline fetch below never reaches —
      // that fetch bails out to the CoinGecko-only branch for those before
      // it ever computes volume stats. Best-effort: falls through silently
      // if the server is unavailable or the coin isn't in its cache yet, in
      // which case the client computation below fills the gap.
      //
      // Previously the client computation ran unconditionally and clobbered
      // this for every Binance-covered coin — the two sources are supposed
      // to agree (both read the same last-closed daily candle), but the
      // server's is only as fresh as its last hourly refresh while the
      // client's is live, so whichever ran last "won" and the dashboard
      // (server-cache only) and this page could disagree for up to an hour.
      // Now the client computation is a true fallback, not a second write.
      let serverVolumeStatsSet = false;
      if (isServerApiAvailable()) {
        try {
          const detail = await fetchServerCoinDetail(id);
          const c = detail?.coin;
          if (c && c.volumeAvg200 != null) {
            setVolumeStats({
              avg200:     c.volumeAvg200,
              todayVol:   c.volumeLatest,
              ratio:      c.volumeRatio,
              spike:      !!c.volumeSpike,
              history30d: c.volumeHistory30d || [],
            });
            serverVolumeStatsSet = true;
          }
        } catch (err) {
          console.warn('[Detail] Server volume stats unavailable for ' + id + ': ' + err.message);
        }
      }

      const binanceSymbol  = getBinanceSymbol(id, coinInfo.symbol);
      const coinbaseSymbol = getCoinbaseSymbol(id);
      const krakenSymbol   = getKrakenSymbol(id);
      const okxSymbol      = getOKXSymbol(id);

      // TradingView symbol: same waterfall as the server's kline fetch —
      // Binance -> Coinbase -> Kraken -> OKX. TradingView pulls its own data
      // directly from each exchange, so this only has to name the right
      // exchange:pair — it doesn't depend on our own kline fetch below.
      setTvSymbol(
        binanceSymbol  ? `BINANCE:${binanceSymbol}` :
        coinbaseSymbol ? `COINBASE:${coinbaseSymbol.replace('-', '')}` :
        krakenSymbol   ? `KRAKEN:${krakenSymbol}` :
        okxSymbol      ? `OKX:${okxSymbol.replace('-', '')}` :
        null
      );

      let klinesResult;
      let exchange = 'binance';

      if (binanceSymbol) {
        console.log('[Detail] Fetching Binance klines for ' + binanceSymbol + '...');
        klinesResult = await fetchKlinesAllTimeframes(binanceSymbol);

        // If Binance returned nothing (futures-only symbol / delisted / mapping error)
        // try Coinbase before giving up.
        if (!klinesResult.fourHour && !klinesResult.daily && !klinesResult.weekly) {
          console.info('[Detail] Binance empty for ' + binanceSymbol + ' — trying Coinbase');
          if (coinbaseSymbol) {
            klinesResult = await fetchCoinbaseKlinesAllTimeframes(coinbaseSymbol);
            exchange = 'coinbase';
          }
        }
      } else if (coinbaseSymbol) {
        console.log('[Detail] Fetching Coinbase klines for ' + coinbaseSymbol + '...');
        klinesResult = await fetchCoinbaseKlinesAllTimeframes(coinbaseSymbol);
        exchange = 'coinbase';
      } else {
        // No OHLCV source — show CoinGecko price chart only, no indicators
        console.info('[Detail] ' + id + ' not on Binance or Coinbase — using CoinGecko chart only');
        const priceHistory = await fetchCoinPriceHistory(id);
        setSparklineData(priceHistory);
        setIndicators('unavailable');
        return;
      }

      const rawFourHour = klinesResult.fourHour;
      const rawDaily    = klinesResult.daily;
      const rawWeekly   = klinesResult.weekly;

      // Final fallback: if all timeframes are still null, show CoinGecko chart
      if (!rawFourHour && !rawDaily && !rawWeekly) {
        console.info('[Detail] No OHLCV data from ' + exchange + ' for ' + id + ' — CoinGecko chart only');
        const priceHistory = await fetchCoinPriceHistory(id);
        setSparklineData(priceHistory);
        setIndicators('unavailable');
        return;
      }

      // Volume stats — Binance field [7] is quote asset volume (USDT)
      // Use the latest Binance daily candle for todayVol so the ratio is
      // apples-to-apples (Binance vs Binance), not CoinGecko vs Binance.
      // Binance always returns the current, still-forming candle as the last
      // element — drop it so the "volume" shown is yesterday's closed candle,
      // not today's partial (and misleadingly low/high) volume-so-far.
      const closedDaily = rawDaily && rawDaily.length > 1 ? rawDaily.slice(0, -1) : null;
      if (!serverVolumeStatsSet && closedDaily && closedDaily.length > 0) {
        const usdVols  = closedDaily.map(function(k) { return parseFloat(k[7]); });
        const avg200   = usdVols.reduce(function(s, v) { return s + v; }, 0) / usdVols.length;
        const todayVol = usdVols[usdVols.length - 1]; // last CLOSED daily Binance candle
        setVolumeStats({
          avg200:     avg200,
          todayVol:   todayVol,
          ratio:      avg200 > 0 ? todayVol / avg200 : null,
          spike:      avg200 > 0 && todayVol / avg200 >= 2,
          history30d: usdVols.slice(-30),
        });
      }

      const fourHourCandles = parseBinanceKlines(rawFourHour);
      const dailyCandles    = parseBinanceKlines(rawDaily);
      const weeklyCandles   = parseBinanceKlines(rawWeekly);

      setSparklineData(parseBinanceKlinesForSparkline(rawFourHour).slice(-60));

      const fourHourIndicators = calculateTimeframeIndicators(extractPrices(fourHourCandles), '4H');
      const dailyIndicators    = calculateTimeframeIndicators(extractPrices(dailyCandles),    'Daily');
      const weeklyIndicators   = calculateTimeframeIndicators(extractPrices(weeklyCandles),   'Weekly');

      setIndicators({
        weekly:   weeklyIndicators,
        daily:    dailyIndicators,
        fourHour: fourHourIndicators,
      });

      console.log('[Detail] Done.');

    } catch (err) {
      console.error('[Detail] Error fetching coin data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack  = () => navigate('/');
  const handleRetry = () => fetchCoinData();

  if (isLoading) {
    return (
      <div className="coin-detail-page">
        <div className="coin-detail-loading">
          <Loader size="large" text="Loading coin data..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="coin-detail-page">
        <ErrorBanner
          message={error}
          type="error"
          onRetry={handleRetry}
          onDismiss={() => setError(null)}
          className="coin-detail-error"
        />
        <div className="back-navigation">
          <button onClick={handleBack} className="back-btn">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (!coinData) {
    return (
      <div className="coin-detail-page">
        <div className="coin-not-found">
          <h2>Cryptocurrency Not Found</h2>
          <p>The requested cryptocurrency could not be found.</p>
          <button onClick={handleBack} className="back-btn">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  const priceChangeClass = coinData.price_change_percentage_24h >= 0 ? 'positive' : 'negative';
  const volRatioClass    = volumeStats && volumeStats.ratio != null
    ? (volumeStats.ratio >= 1 ? 'vol-high' : 'vol-low')
    : '';

  return (
    <div className="coin-detail-page">
      <OnboardingTour
        run={showCoinDetailOnboarding}
        steps={COIN_DETAIL_TOUR_STEPS}
        onDismiss={dismissCoinDetailOnboarding}
      />

      <div className="back-navigation">
        <button onClick={handleBack} className="back-btn">Back to Dashboard</button>
      </div>

      <div className="coin-header">
        <div className="coin-header-main">
          <div className="coin-logo-section">
            {coinData.image ? (
              <img
                src={coinData.image}
                alt={coinData.name}
                className="coin-header-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="coin-header-logo-fallback"
              style={{ display: coinData.image ? 'none' : 'flex' }}
            >
              {coinData.symbol ? coinData.symbol.charAt(0).toUpperCase() : '?'}
            </div>
          </div>
          <div className="coin-header-info">
            <h1 className="coin-title">
              {coinData.name}
              <span className="coin-symbol-header">({coinData.symbol ? coinData.symbol.toUpperCase() : ''})</span>
            </h1>
            <div className="coin-rank">Rank #{coinData.market_cap_rank || 'N/A'}</div>
          </div>
        </div>
        <div className="coin-header-stats">
          <div className="price-section">
            <div className="current-price">{formatPrice(coinData.current_price)}</div>
            <div className={"price-change " + priceChangeClass}>
              {formatPercentChange(coinData.price_change_percentage_24h)} (24h)
            </div>
          </div>
        </div>
      </div>

      <div className="coin-stats-grid">
        <div className="coin-stat-item">
          <div className="stat-item-label">Market Cap</div>
          <div className="stat-item-value">{formatMarketCap(coinData.market_cap)}</div>
        </div>
        <div className="coin-stat-item">
          <div className="stat-item-label">24h High</div>
          <div className="stat-item-value">{formatPrice(coinData.high_24h)}</div>
        </div>
        <div className="coin-stat-item">
          <div className="stat-item-label">24h Low</div>
          <div className="stat-item-value">{formatPrice(coinData.low_24h)}</div>
        </div>
        <div className="coin-stat-item">
          <div className="stat-item-label">24h Change</div>
          <div className={"stat-item-value " + priceChangeClass}>
            {formatPercentChange(coinData.price_change_percentage_24h)}
          </div>
        </div>
      </div>

      <div className="volume-section">
        <h3 className="volume-section-title">
          Volume Analysis
          {volumeStats && volumeStats.spike && (
            <span className="volume-spike-badge">Spike</span>
          )}
        </h3>
        <div className="volume-stats-row">
          <div className="volume-stat">
            <div className="volume-stat-label">
              {volumeStats ? "Yesterday's Volume (Binance)" : '24h Volume'}
            </div>
            <div className={"volume-stat-value " + volRatioClass}>
              {volumeStats ? formatVolume(volumeStats.todayVol) : formatVolume(coinData.total_volume)}
            </div>
          </div>
          <div className="volume-stat">
            <div className="volume-stat-label">200-Day Average</div>
            <div className="volume-stat-value">
              {volumeStats ? formatVolume(volumeStats.avg200) : '—'}
            </div>
          </div>
          <div className="volume-stat">
            <div className="volume-stat-label">Vol / Avg Ratio</div>
            <div className={"volume-stat-value " + volRatioClass}>
              {volumeStats && volumeStats.ratio != null
                ? volumeStats.ratio.toFixed(2) + 'x'
                : '—'}
            </div>
          </div>
        </div>

        {volumeStats && volumeStats.ratio != null && (
          <div className="volume-ratio-bar-wrap">
            <div className="volume-ratio-bar-track">
              <div
                className={"volume-ratio-bar-fill " + volRatioClass}
                style={{ width: (Math.min(volumeStats.ratio / 3, 1) * 100) + '%' }}

              />
              <div className="volume-ratio-bar-marker" style={{ left: '33.33%' }} />
              <div className="volume-ratio-bar-marker" style={{ left: '66.67%' }} />
            </div>
            <div className="volume-ratio-bar-labels">
              <span>0x</span>
              <span>1x avg</span>
              <span>2x avg</span>
              <span>3x+</span>
            </div>
          </div>
        )}

        {volumeStats && volumeStats.history30d && volumeStats.history30d.length > 1 && (
          <div className="volume-sparkline-wrap">
            <div className="volume-sparkline-label">30-Day Daily Volume</div>
            <VolumeSparkline volumes={volumeStats.history30d} avg={volumeStats.avg200} />
          </div>
        )}
      </div>

      {/* Candlestick Chart — TradingView widget */}
      <div className="chart-section">
        <div className="chart-header">
          <h3>Price Chart</h3>
          {tvSymbol && (
            <div className="chart-interval-tabs">
              {TV_INTERVALS.map(({ label, value }) => (
                <button
                  key={value}
                  className={`chart-interval-tab${chartInterval === value ? ' active' : ''}`}
                  onClick={() => setChartInterval(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="tv-chart-container">
          {tvSymbol ? (
            <TradingViewChart symbol={tvSymbol} interval={chartInterval} />
          ) : (
            <SparklineChart
              data={sparklineData}
              width={800}
              height={200}
              className="coin-sparkline"
            />
          )}
        </div>
      </div>

      {/* About & News */}
      {isServerApiAvailable() && (
        <div className="coin-about-section">
          <h3 className="coin-about-title">About {coinData.name}</h3>

          {coinInfoLoading && (
            <div className="coin-about-loading">
              <Loader size="small" text="Loading project info..." />
            </div>
          )}

          {!coinInfoLoading && plainDescription && (
            <div className="coin-about-description">
              <p>
                {descriptionExpanded || plainDescription.length <= DESCRIPTION_PREVIEW_LENGTH
                  ? plainDescription
                  : plainDescription.slice(0, DESCRIPTION_PREVIEW_LENGTH).trim() + '…'}
              </p>
              {plainDescription.length > DESCRIPTION_PREVIEW_LENGTH && (
                <button
                  className="coin-about-toggle"
                  onClick={() => setDescriptionExpanded((v) => !v)}
                >
                  {descriptionExpanded ? 'Show less' : 'Read more'}
                </button>
              )}
            </div>
          )}

          {!coinInfoLoading && !plainDescription && (
            <p className="coin-about-empty">No project description available.</p>
          )}

          {coinInfo?.homepage && (
            <a
              href={coinInfo.homepage}
              target="_blank"
              rel="noopener noreferrer"
              className="coin-about-homepage"
            >
              Visit official website ↗
            </a>
          )}

          <h3 className="coin-news-title">Latest News</h3>

          {!coinInfoLoading && coinInfo?.news?.length > 0 && (
            <div className="coin-news-list">
              {coinInfo.news.map((article, i) => (
                <a
                  key={article.url || i}
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="coin-news-item"
                >
                  {article.imageUrl && (
                    <img
                      src={article.imageUrl}
                      alt=""
                      className="coin-news-thumb"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="coin-news-body">
                    <div className="coin-news-headline">{article.title}</div>
                    <div className="coin-news-meta">
                      {article.source && <span>{article.source}</span>}
                      {article.publishedAt && <span>{article.source ? ' · ' : ''}{formatNewsDate(article.publishedAt)}</span>}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {!coinInfoLoading && (!coinInfo || !coinInfo.news || coinInfo.news.length === 0) && (
            <p className="coin-about-empty">No recent news found for {coinData.name}.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default CoinDetailPage;
